import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import { buildLearningTopicPath } from '../../lib/educationContent';

const eventLabels = {
  topic_viewed: 'Viewed',
  topic_expanded: 'Expanded Help',
  resource_opened: 'Opened Resource',
  article_completed: 'Completed Article',
  topic_bookmarked: 'Saved',
  marked_understood: 'Understood',
  marked_confusing: 'Question',
  advisor_discussion_requested: 'Ask Advisor',
  attorney_discussion_requested: 'Ask Attorney'
};

const feedbackLabels = {
  understood: 'Understood',
  confusing: 'Question',
  ask_advisor: 'Ask Advisor',
  ask_attorney: 'Ask Attorney',
  saved: 'Saved'
};

const feedbackStyles = {
  understood: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  confusing: 'bg-amber-50 text-amber-700 border-amber-100',
  ask_advisor: 'bg-blue-50 text-blue-700 border-blue-100',
  ask_attorney: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  saved: 'bg-slate-100 text-slate-600 border-slate-200'
};

const getTopic = (row) => {
  const topic = row?.topic;
  return Array.isArray(topic) ? topic[0] : topic;
};

const getResource = (row) => {
  const resource = row?.resource;
  return Array.isArray(resource) ? resource[0] : resource;
};

const countEvents = (events, eventType) => events.filter((event) => event.event_type === eventType).length;

const ProfessionalLearningInsights = ({ matter }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!matter?.id) return;

      try {
        setLoading(true);
        setError(null);

        const [eventsRes, feedbackRes] = await Promise.all([
          supabase
            .from('education_learning_events')
            .select(`
              id,
              event_type,
              question_key,
              topic_id,
              resource_id,
              created_at,
              topic:education_topics(id, title, slug, content_category),
              resource:education_resources(id, title, resource_type)
            `)
            .eq('matter_id', matter.id)
            .order('created_at', { ascending: false })
            .limit(50),
          supabase
            .from('education_topic_feedback')
            .select(`
              id,
              feedback_type,
              created_at,
              updated_at,
              topic_id,
              topic:education_topics(id, title, slug, content_category)
            `)
            .eq('matter_id', matter.id)
            .order('updated_at', { ascending: false })
        ]);

        if (eventsRes.error) throw eventsRes.error;
        if (feedbackRes.error) throw feedbackRes.error;

        setEvents(eventsRes.data || []);
        setFeedback(feedbackRes.data || []);
      } catch (err) {
        console.error('Professional learning insights error:', err);
        setError(err.message || 'Unable to load client learning insights.');
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [matter?.id]);

  const summary = useMemo(() => {
    const viewedTopicIds = new Set(
      events
        .filter((event) => event.event_type === 'topic_viewed' && event.topic_id)
        .map((event) => event.topic_id)
    );

    return {
      viewedTopics: viewedTopicIds.size,
      completedArticles: countEvents(events, 'article_completed'),
      savedTopics: feedback.filter((row) => row.feedback_type === 'saved').length,
      questions: feedback.filter((row) => ['confusing', 'ask_advisor', 'ask_attorney'].includes(row.feedback_type)).length
    };
  }, [events, feedback]);

  const feedbackByTopic = useMemo(() => {
    return feedback.reduce((map, row) => {
      const topic = getTopic(row);
      if (!topic?.id) return map;

      if (!map[topic.id]) {
        map[topic.id] = {
          topic,
          types: [],
          updated_at: row.updated_at || row.created_at
        };
      }

      map[topic.id].types.push(row.feedback_type);
      if (new Date(row.updated_at || row.created_at) > new Date(map[topic.id].updated_at)) {
        map[topic.id].updated_at = row.updated_at || row.created_at;
      }

      return map;
    }, {});
  }, [feedback]);

  const discussionTopics = useMemo(() => {
    return Object.values(feedbackByTopic)
      .filter((item) => item.types.some((type) => ['confusing', 'ask_advisor', 'ask_attorney'].includes(type)))
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }, [feedbackByTopic]);

  const recentEvents = events.slice(0, 6);

  const openTopic = (topic) => {
    if (topic?.slug) navigate(buildLearningTopicPath({ slug: topic.slug, matterId: matter?.id }));
  };

  if (loading) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center text-sm text-slate-500">
          <SafeIcon icon={FiIcons.FiLoader} className="mr-2 h-4 w-4 animate-spin text-slate-300" />
          Loading client learning insights...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-xl border border-amber-100 bg-amber-50 p-6 shadow-sm">
        <h3 className="flex items-center text-sm font-bold uppercase tracking-tight text-amber-900">
          <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-2 h-4 w-4 text-amber-600" />
          Client Learning Insights
        </h3>
        <p className="mt-2 text-sm text-amber-800">{error}</p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
        <h3 className="flex items-center text-sm font-bold uppercase tracking-tight text-slate-800">
          <SafeIcon icon={FiIcons.FiBookOpen} className="mr-2 h-4 w-4 text-slate-400" />
          Client Learning Insights
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Read-only signals from the client's Guided Learning activity for this plan.
        </p>
      </div>

      <div className="p-6">
        {events.length === 0 && feedback.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
            <SafeIcon icon={FiIcons.FiBookOpen} className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">No learning activity yet.</p>
            <p className="mt-1 text-xs text-slate-500">
              Activity will appear here as the client opens topics, saves items, or marks questions.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Topics Viewed</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{summary.viewedTopics}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Completed</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{summary.completedArticles}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Saved</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{summary.savedTopics}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Needs Discussion</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{summary.questions}</p>
              </div>
            </div>

            {discussionTopics.length > 0 && (
              <div>
                <h4 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">Discussion Signals</h4>
                <div className="space-y-3">
                  {discussionTopics.map((item) => (
                    <div key={item.topic.id} className="rounded-lg border border-amber-100 bg-amber-50/40 p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <button
                            type="button"
                            onClick={() => openTopic(item.topic)}
                            className="text-left text-sm font-bold text-slate-900 hover:text-blue-700"
                          >
                            {item.topic.title}
                          </button>
                          <p className="mt-1 text-xs text-slate-500">{item.topic.content_category}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {item.types.map((type) => (
                            <span
                              key={type}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${feedbackStyles[type] || feedbackStyles.saved}`}
                            >
                              {feedbackLabels[type] || type}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recentEvents.length > 0 && (
              <div>
                <h4 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">Recent Activity</h4>
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
                  {recentEvents.map((event) => {
                    const topic = getTopic(event);
                    const resource = getResource(event);
                    const title = topic?.title || resource?.title || event.question_key || 'Learning activity';
                    return (
                      <div key={event.id} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{title}</p>
                          <p className="text-xs text-slate-500">{eventLabels[event.event_type] || event.event_type}</p>
                        </div>
                        <span className="text-xs text-slate-400">
                          {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfessionalLearningInsights;
